import React, { useCallback, useEffect, useState } from "react";

import { Link, useHistory } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";

import { BrComerceApiService } from "../../../../../services/BrComerceApiService";
import Loading from "../../../../home/components/Loading";
import Container from "../../../components/Container";
import { selectStyle } from "../../../../../components/InputSelectAsync";
import store from "../../../../../store/store";

import { FaCirclePlus } from "react-icons/fa6";
import { FiDownload, FiRefreshCw } from "react-icons/fi";

import styles from "./freightList.module.scss";

const TODOS = { label: "Todos", value: "" };

const integrations = [
  TODOS,
  { label: "VTEX", value: "vtex" },
  { label: "Bagy", value: "bagy" },
  { label: "VNDA", value: "vnda" },
  { label: "Intelipost", value: "intelipost" }
];

const services = [
  TODOS,
  { label: "Super Expressa", value: "OWN_DELIVERY" },
  { label: "Expressa", value: "SEDEX" },
  { label: "Econômica", value: "PAC" }
];

const audiences = [
  TODOS,
  { label: "Varejo", value: "varejo" },
  { label: "Industrial", value: "industrial" }
];

const origins = [
  TODOS,
  { label: "Rio de Janeiro", value: "RJ" },
  { label: "São Paulo", value: "SP" }
];

// timeZone fixo: a data tem que ser a mesma para quem abre a tela de qualquer lugar
const formatDate = value =>
  (value
    ? new Date(value).toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    })
    : "—");

const FreightList = () => {
  const api = new BrComerceApiService();
  const history = useHistory();

  const { auth } = store.getState();
  const { user } = auth;
  const isAdmin = user?.profile === "admin";

  const [isLoading, setIsLoading] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [integration, setIntegration] = useState(TODOS);
  const [service, setService] = useState(TODOS);
  const [audience, setAudience] = useState(TODOS);
  const [origin, setOrigin] = useState(TODOS);

  const loadCatalog = useCallback(async () => {
    try {
      setIsLoading(true);

      const result = await api.makeHttpRequest({ method: "GET", url: "/rates-sheet/catalog" });

      setCatalog(result || []);
    } catch (e) {
      Swal.fire({ title: "Erro!", text: "Não foi possível carregar o catálogo", icon: "error" });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.setPageTitle("Tabelas de Frete");

    if (!isAdmin) {
      history.push("/");

      return;
    }

    loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCatalog]);

  const visible = catalog.filter(item =>
    (!integration.value || item.integration === integration.value)
    && (!service.value || item.service === service.value)
    && (!audience.value || item.audience === audience.value)
    && (!origin.value || item.originUf === origin.value));

  const handleDownload = item => {
    if (!item.link) {
      Swal.fire({
        title: "Ainda não gerada",
        text: "Essa combinação ainda não tem planilha. Use o regerar para gerá-la.",
        icon: "info"
      });
      return;
    }

    window.open(item.link, "_blank");
  };

  const handleRegenerate = async item => {
    const confirm = await Swal.fire({
      title: item.generatedAt ? "Regerar planilha?" : "Gerar planilha?",
      html: `${item.serviceLabel} · ${item.audience} · ${item.originUf} · ${item.integration.toUpperCase()}<br><br>`
        + (item.generatedAt
          ? "A planilha será gerada com a cobertura atual e substituirá a disponível para download."
          : "Essa combinação ainda não existe e será gerada do zero."),
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      confirmButtonText: item.generatedAt ? "Sim, regerar" : "Sim, gerar"
    });

    if (!confirm?.value) {
      return;
    }

    try {
      setIsLoading(true);

      await api.makeHttpRequest({
        method: "POST",
        url: "/rates-sheet/regenerate",
        data: {
          integration: item.integration,
          service: item.service,
          originUf: item.originUf,
          isIndustrial: item.isIndustrial
        }
      });

      Swal.fire({
        title: "Geração iniciada",
        text: "A planilha fica disponível assim que terminar. Atualize a lista em alguns minutos.",
        icon: "success"
      });
    } catch (e) {
      Swal.fire({ title: "Erro!", text: "Não foi possível iniciar a geração", icon: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateGroup = async group => {
    const label = group === "super-expressa" ? "as Super Expressas" : "as demais planilhas";

    const confirm = await Swal.fire({
      title: `Regerar ${label}?`,
      html: `Todas as combinações de ${label} serão geradas de novo, uma de cada vez.<br>Isso leva bastante tempo e cada planilha fica disponível conforme termina.`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sim, regerar"
    });

    if (!confirm?.value) {
      return;
    }

    try {
      setIsLoading(true);

      await api.makeHttpRequest({ method: "POST", url: "/rates-sheet/regenerate-all", data: { group } });

      Swal.fire({
        title: "Geração iniciada",
        text: "As planilhas são atualizadas conforme cada uma termina.",
        icon: "success"
      });
    } catch (e) {
      Swal.fire({ title: "Erro!", text: "Não foi possível iniciar a geração", icon: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Loading isLoading={isLoading} />
      <Container title="Tabelas de Frete">
        <div className={styles.contentBody}>
          <div className={styles.filters}>
            <Link to="/tabelas-frete/create">
              <button type="button" className={styles.btnAddTable}>
                <FaCirclePlus fill="#fff" color="#fff" />
                <span>Criar tabela avulsa</span>
              </button>
            </Link>
            <button type="button" className={styles.btnQuickRegenerate} onClick={() => handleRegenerateGroup("super-expressa")}>
              <FiRefreshCw />
              <span>Regerar Super Expressas</span>
            </button>
            <button type="button" className={styles.btnQuickRegenerate} onClick={() => handleRegenerateGroup("outras")}>
              <FiRefreshCw />
              <span>Regerar outras</span>
            </button>
            <button type="button" className={styles.btnQuickRegenerate} onClick={loadCatalog}>
              <span>Atualizar lista</span>
            </button>
          </div>

          <div className={styles.quickDownload}>
            <div className={styles.quickHeader}>
              <strong>Catálogo de planilhas</strong>
              <span>
                {`${visible.length} de ${catalog.length} combinações · baixe a planilha pronta ou regere com a cobertura atual`}
              </span>
            </div>
            <div className={styles.quickFilters}>
              <div className={styles.quickInput}>
                <Select options={integrations} value={integration} placeholder="Plataforma" styles={selectStyle} onChange={setIntegration} />
              </div>
              <div className={styles.quickInput}>
                <Select options={services} value={service} placeholder="Serviço" styles={selectStyle} onChange={setService} />
              </div>
              <div className={styles.quickInput}>
                <Select options={audiences} value={audience} placeholder="Tipo de cotação" styles={selectStyle} onChange={setAudience} />
              </div>
              <div className={styles.quickInput}>
                <Select options={origins} value={origin} placeholder="Origem" styles={selectStyle} onChange={setOrigin} />
              </div>
            </div>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.catalogTable}>
              <thead>
                <tr>
                  <th>Plataforma</th>
                  <th>Serviço</th>
                  <th>Tipo de cotação</th>
                  <th>Origem</th>
                  <th>Gerada em</th>
                  <th className={styles.acoes}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(item => (
                  <tr key={item.name}>
                    <td>{item.integration.toUpperCase()}</td>
                    <td>{item.serviceLabel}</td>
                    <td>{item.isIndustrial ? "Industrial" : "Varejo"}</td>
                    <td>{item.originUf}</td>
                    <td className={item.generatedAt ? undefined : styles.semData}>{formatDate(item.generatedAt)}</td>
                    <td className={styles.acoes}>
                      <div className={styles.rowActions}>
                        <button
                          type="button"
                          className={styles.baixar}
                          disabled={!item.link}
                          title={item.link ? "Baixar planilha" : "Ainda não gerada"}
                          onClick={() => handleDownload(item)}
                        >
                          <FiDownload />
                          <span>Baixar</span>
                        </button>
                        <button
                          type="button"
                          title={item.generatedAt ? "Regerar com a cobertura atual" : "Gerar pela primeira vez"}
                          onClick={() => handleRegenerate(item)}
                        >
                          <FiRefreshCw />
                          <span>{item.generatedAt ? "Regerar" : "Gerar"}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!visible.length && (
                  <tr>
                    <td colSpan={6} className={styles.vazio}>Nenhuma combinação para os filtros escolhidos.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Container>
    </>
  );
};

export { FreightList };
